/**
 * SFDRFunds — SFDR (Regulation (EU) 2019/2088) Art. 6/8/9 fund classification
 * plus per-fund Taxonomy-Regulation (EU) 2020/852 alignment disclosure.
 *
 * Classification is DERIVED, never editable directly: a sustainable-investment
 * objective yields Art. 9 (overriding an E/S promotion flag), promoting E/S
 * characteristics yields Art. 8, and neither yields Art. 6. Chrome comes
 * entirely from EsgKit. API contract lives in
 * backend/routes/sfdr_funds_routes.py + services/sfdr_funds_service.py.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Landmark, CircleDashed, Scale, Target, CheckCircle2, MinusCircle,
  Plus, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt,
  C, MONO, SANS,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";

const BASE = "/api/sfdr/funds";

// ─── Article badge vocabulary (shared everywhere on the page) ──────────────────
const ARTICLE_META = {
  article_6: { tone: C.neutral, Icon: CircleDashed, label: "Art. 6" },
  article_8: { tone: C.locked, Icon: Scale, label: "Art. 8" },
  article_9: { tone: C.primary, Icon: Target, label: "Art. 9" },
};

function ArticleBadge({ article }) {
  const m = ARTICLE_META[article] || ARTICLE_META.article_6;
  return <Badge c={m.tone} Icon={m.Icon} label={m.label} />;
}

// PAI-consideration status: color + icon + label, never color alone.
function PaiBadge({ considered }) {
  return considered
    ? <Badge c={C.primary} Icon={CheckCircle2} label="PAI" />
    : <Badge c={C.neutral} Icon={MinusCircle} label="No PAI" />;
}

// Percentage cell for the table: mono, right-aligned, "—" when null.
const pctCell = (v) => (
  <Data color={C.body}>{v === null || v === undefined ? "—" : `${fmt(v)}%`}</Data>
);

// Clamp a percentage-input string to 0–100; blank stays blank (→ null on submit).
const validPct = (v) => {
  if (v === "" || v === null || v === undefined) return true;
  const n = Number(v);
  return !isNaN(n) && n >= 0 && n <= 100;
};

export default function SFDRFunds() {
  const { entities, entityId, setEntityId } = useEntities("sfdr_funds_entity_id");

  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [classification, setClassification] = useState(null);
  const [classLoading, setClassLoading] = useState(false);

  const [showNew, setShowNew] = useState(false);

  // ─── Data loading ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    try {
      const r = await apiFetch(`${BASE}?entity_id=${encodeURIComponent(entityId)}`);
      setFunds(r?.funds || []);
    } catch (e) {
      setError(e.message || "Failed to load funds");
      setFunds([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  const loadClassification = useCallback(async (fundId) => {
    if (!fundId) {
      setClassification(null);
      return;
    }
    setClassLoading(true);
    try {
      const r = await apiFetch(`${BASE}/${encodeURIComponent(fundId)}/classification`);
      setClassification(r);
    } catch (e) {
      setError(e.message || "Failed to load classification");
      setClassification(null);
    } finally {
      setClassLoading(false);
    }
  }, []);

  useEffect(() => {
    // Entity switch resets the selected fund.
    setSelectedId(null);
    setClassification(null);
    load();
  }, [load]);

  const selectFund = (fundId) => {
    setSelectedId(fundId);
    loadClassification(fundId);
  };

  const selectedFund = funds.find((f) => f.fund_id === selectedId) || null;

  // ─── Mutations ────────────────────────────────────────────────────────────
  const afterCreate = async (newId) => {
    await load();
    if (newId) selectFund(newId);
  };

  const afterDisclosure = async () => {
    await load();
    if (selectedId) await loadClassification(selectedId);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Page>
      <PageHeader
        overline="SFDR · Fund Classification"
        OverlineIcon={Landmark}
        title="SFDR Funds"
        subtitle="Article 6/8/9 classification derived from declared E/S characteristics and sustainable-investment objectives, plus Taxonomy-alignment disclosure."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
      />

      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {!entityId ? (
        <Card>
          <EmptyState
            Icon={Landmark}
            title="Select a reporting entity"
            body="Choose an entity above to view and classify its SFDR fund products."
          />
        </Card>
      ) : loading ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={220} />
          <Skeleton h={120} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <FundsCard funds={funds} selectedId={selectedId} onSelect={selectFund} />

          <NewFundCard
            open={showNew}
            onToggle={() => setShowNew((v) => !v)}
            entityId={entityId}
            onCreated={afterCreate}
            onError={setError}
          />

          {selectedFund && (
            <SelectedFundPanel
              fund={selectedFund}
              classification={classification}
              loading={classLoading}
              onDisclosed={afterDisclosure}
              onError={setError}
            />
          )}
        </div>
      )}
    </Page>
  );
}

// ─── Funds table ────────────────────────────────────────────────────────────
function FundsCard({ funds, selectedId, onSelect }) {
  const columns = [
    {
      key: "name",
      label: "Fund",
      render: (f) => (
        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.ink }}>
          {f.name}
        </span>
      ),
    },
    {
      key: "article",
      label: "Article",
      render: (f) => <ArticleBadge article={f.classification?.article} />,
    },
    {
      key: "min_sustainable_investment_pct",
      label: "Min. sustainable inv.",
      align: "right",
      render: (f) => pctCell(f.min_sustainable_investment_pct),
    },
    {
      key: "min_taxonomy_aligned_pct",
      label: "Min. taxonomy-aligned",
      align: "right",
      render: (f) => pctCell(f.min_taxonomy_aligned_pct),
    },
    {
      key: "pai_considered",
      label: "PAI",
      render: (f) => <PaiBadge considered={f.pai_considered} />,
    },
  ];

  return (
    <Card>
      <Overline Icon={Landmark}>Funds</Overline>
      <SelectableTable
        columns={columns}
        rows={funds}
        keyField="fund_id"
        selectedId={selectedId}
        onSelect={onSelect}
        emptyLabel="No funds registered for this entity"
      />
    </Card>
  );
}

// DataTable renders its own rows; for row selection + highlight we mirror its
// cell idiom (th/td styling comes from the kit's DataTable) with a thin wrapper.
function SelectableTable({ columns, rows, keyField, selectedId, onSelect, emptyLabel }) {
  const th = {
    textAlign: "left", padding: "8px 10px", fontFamily: SANS, fontSize: 11,
    fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.border}`,
    whiteSpace: "nowrap",
  };
  const tdBase = {
    padding: "9px 10px", borderBottom: `1px solid ${C.border}`,
    verticalAlign: "middle",
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{ ...th, textAlign: col.align || "left" }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{ ...tdBase, fontFamily: MONO, fontSize: 12, color: C.muted, textAlign: "center", borderBottom: "none" }}
              >
                {emptyLabel}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const isSel = row[keyField] === selectedId;
              return (
                <tr
                  key={row[keyField]}
                  onClick={() => onSelect(row[keyField])}
                  style={{
                    cursor: "pointer",
                    background: isSel ? `${C.primary}12` : "transparent",
                    boxShadow: isSel ? `inset 3px 0 0 ${C.primary}` : "none",
                  }}
                  onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = C.surfaceHi; }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                >
                  {columns.map((col) => (
                    <td key={col.key} style={{ ...tdBase, textAlign: col.align || "left" }}>
                      {col.render ? col.render(row) : <Data color={C.body}>{row[col.key] ?? "—"}</Data>}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── New fund (collapsible inline form) ─────────────────────────────────────
const EMPTY_FUND = {
  name: "",
  strategy_description: "",
  promotes_es_characteristics: false,
  sustainable_investment_objective: false,
  min_sustainable_investment_pct: "",
  min_taxonomy_aligned_pct: "",
  pai_considered: false,
};

function NewFundCard({ open, onToggle, entityId, onCreated, onError }) {
  const [form, setForm] = useState(EMPTY_FUND);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const pctInvalid =
    !validPct(form.min_sustainable_investment_pct) ||
    !validPct(form.min_taxonomy_aligned_pct);
  const canSubmit = form.name.trim() !== "" && !pctInvalid && !submitting;

  const toNum = (v) => (v === "" ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const created = await apiFetch(BASE, {
        method: "POST",
        body: {
          entity_id: entityId,
          name: form.name.trim(),
          strategy_description: form.strategy_description,
          promotes_es_characteristics: form.promotes_es_characteristics,
          sustainable_investment_objective: form.sustainable_investment_objective,
          min_sustainable_investment_pct: toNum(form.min_sustainable_investment_pct),
          min_taxonomy_aligned_pct: toNum(form.min_taxonomy_aligned_pct),
          pai_considered: form.pai_considered,
        },
      });
      setForm(EMPTY_FUND);
      onToggle(); // collapse
      await onCreated(created?.fund_id);
    } catch (err) {
      onError(err.message || "Failed to create fund");
    } finally {
      setSubmitting(false);
    }
  };

  // Derived-article hint mirrors the backend precedence rule.
  const previewArticle = form.sustainable_investment_objective
    ? "article_9"
    : form.promotes_es_characteristics
    ? "article_8"
    : "article_6";

  return (
    <Card>
      <button
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", gap: 7, width: "100%",
          background: "transparent", border: "none", cursor: "pointer", padding: 0,
          fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
          textTransform: "uppercase", color: C.body,
        }}
      >
        {open ? <ChevronDown size={14} strokeWidth={1.5} /> : <ChevronRight size={14} strokeWidth={1.5} />}
        New fund
      </button>

      {open && (
        <form onSubmit={submit} style={{ marginTop: 16, display: "grid", gap: 14 }}>
          <div>
            <Label>Fund name *</Label>
            <input
              style={field}
              value={form.name}
              onChange={set("name")}
              placeholder="Global Sustainable Equity Fund"
            />
          </div>

          <div>
            <Label>Strategy description</Label>
            <textarea
              style={{ ...field, minHeight: 72, fontFamily: SANS, resize: "vertical" }}
              value={form.strategy_description}
              onChange={set("strategy_description")}
              placeholder="Investment approach, screening, and objectives…"
            />
          </div>

          <label style={checkRow}>
            <input type="checkbox" checked={form.promotes_es_characteristics} onChange={set("promotes_es_characteristics")} />
            <span style={checkLabel}>Promotes environmental/social characteristics → Art. 8</span>
          </label>

          <label style={checkRow}>
            <input type="checkbox" checked={form.sustainable_investment_objective} onChange={set("sustainable_investment_objective")} />
            <span style={checkLabel}>Sustainable investment objective → Art. 9, overrides Art. 8</span>
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label>Min. sustainable investment %</Label>
              <input
                style={{ ...field, borderColor: validPct(form.min_sustainable_investment_pct) ? C.border : C.destructive }}
                type="number" min={0} max={100} step="0.01"
                value={form.min_sustainable_investment_pct}
                onChange={set("min_sustainable_investment_pct")}
                placeholder="blank → null"
              />
            </div>
            <div>
              <Label>Min. taxonomy-aligned %</Label>
              <input
                style={{ ...field, borderColor: validPct(form.min_taxonomy_aligned_pct) ? C.border : C.destructive }}
                type="number" min={0} max={100} step="0.01"
                value={form.min_taxonomy_aligned_pct}
                onChange={set("min_taxonomy_aligned_pct")}
                placeholder="blank → null"
              />
            </div>
          </div>

          <label style={checkRow}>
            <input type="checkbox" checked={form.pai_considered} onChange={set("pai_considered")} />
            <span style={checkLabel}>Considers principal adverse impacts (PAI)</span>
          </label>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>Derived classification:</span>
            <ArticleBadge article={previewArticle} />
          </div>

          <p style={{ fontFamily: SANS, fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.5 }}>
            Classification is derived: objective → Art. 9; promotes E/S → Art. 8; neither → Art. 6.
          </p>

          <div>
            <Btn type="submit" variant="primary" Icon={Plus} disabled={!canSubmit}>
              {submitting ? "Creating…" : "Create fund"}
            </Btn>
          </div>
        </form>
      )}
    </Card>
  );
}

const checkRow = {
  display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer",
  fontFamily: SANS, fontSize: 13, color: C.ink,
};
const checkLabel = { lineHeight: 1.4 };

// ─── Selected fund + taxonomy disclosure ────────────────────────────────────
function SelectedFundPanel({ fund, classification, loading, onDisclosed, onError }) {
  const article = classification?.article || fund.classification?.article;
  const notes = classification?.notes || [];
  const minSust =
    classification?.declared_min_sustainable_investment_pct ??
    fund.min_sustainable_investment_pct;
  const minTax =
    classification?.declared_min_taxonomy_aligned_pct ??
    fund.min_taxonomy_aligned_pct;
  const pai =
    classification?.pai_considered ?? fund.pai_considered;

  return (
    <Card accent={C.primary}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
        <Overline Icon={Landmark} style={{ marginBottom: 0 }}>Classification</Overline>
        <ArticleBadge article={article} />
      </div>

      <h2 style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif", fontSize: 18, fontWeight: 700, color: C.ink, margin: "8px 0 14px" }}>
        {fund.name}
      </h2>

      {loading ? (
        <Skeleton h={90} />
      ) : (
        <>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 14 }}>
            <Metric
              label="Min. sustainable investment"
              value={minSust === null || minSust === undefined ? "—" : fmt(minSust)}
              unit={minSust === null || minSust === undefined ? undefined : "%"}
            />
            <Metric
              label="Min. taxonomy-aligned"
              value={minTax === null || minTax === undefined ? "—" : fmt(minTax)}
              unit={minTax === null || minTax === undefined ? undefined : "%"}
            />
            <div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>PAI consideration</div>
              <PaiBadge considered={pai} />
            </div>
          </div>

          {classification?.basis && (
            <p style={{ fontFamily: SANS, fontSize: 12, color: C.body, margin: "0 0 12px", lineHeight: 1.5 }}>
              {classification.basis}
            </p>
          )}

          {notes.length > 0 && (
            <ul style={{ listStyle: "none", margin: "0 0 4px", padding: 0, display: "grid", gap: 6 }}>
              {notes.map((n, i) => (
                <li key={i} style={{ fontFamily: MONO, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                  · {n}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, margin: "16px 0 0", paddingTop: 16 }}>
        <TaxonomyDisclosureForm fund={fund} onDisclosed={onDisclosed} onError={onError} />
      </div>
    </Card>
  );
}

const EMPTY_DISCLOSURE = {
  taxonomy_aligned_pct: "",
  enabling_pct: "",
  transitional_pct: "",
};

function TaxonomyDisclosureForm({ fund, onDisclosed, onError }) {
  const [form, setForm] = useState(EMPTY_DISCLOSURE);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset when switching funds.
  useEffect(() => {
    setForm(EMPTY_DISCLOSURE);
    setResult(null);
  }, [fund.fund_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const alignedInvalid =
    form.taxonomy_aligned_pct === "" || !validPct(form.taxonomy_aligned_pct);
  const enablingInvalid = !validPct(form.enabling_pct);
  const transitionalInvalid = !validPct(form.transitional_pct);
  const canSubmit =
    !alignedInvalid && !enablingInvalid && !transitionalInvalid && !submitting;

  const toNum = (v) => (v === "" ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await apiFetch(`${BASE}/${encodeURIComponent(fund.fund_id)}/taxonomy-disclosure`, {
        method: "POST",
        body: {
          taxonomy_aligned_pct: Number(form.taxonomy_aligned_pct),
          enabling_pct: toNum(form.enabling_pct),
          transitional_pct: toNum(form.transitional_pct),
        },
      });
      setResult(r);
      await onDisclosed();
    } catch (err) {
      onError(err.message || "Failed to record taxonomy disclosure");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (invalid) => ({
    ...field,
    borderColor: invalid ? C.destructive : C.border,
  });

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
      <Overline Icon={Scale}>Taxonomy disclosure</Overline>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <div>
          <Label>Taxonomy-aligned % *</Label>
          <input
            style={inputStyle(alignedInvalid && form.taxonomy_aligned_pct !== "")}
            type="number" min={0} max={100} step="0.01"
            value={form.taxonomy_aligned_pct}
            onChange={set("taxonomy_aligned_pct")}
            placeholder="0–100"
          />
        </div>
        <div>
          <Label>Of which enabling %</Label>
          <input
            style={inputStyle(enablingInvalid)}
            type="number" min={0} max={100} step="0.01"
            value={form.enabling_pct}
            onChange={set("enabling_pct")}
            placeholder="0–100"
          />
        </div>
        <div>
          <Label>Of which transitional %</Label>
          <input
            style={inputStyle(transitionalInvalid)}
            type="number" min={0} max={100} step="0.01"
            value={form.transitional_pct}
            onChange={set("transitional_pct")}
            placeholder="0–100"
          />
        </div>
      </div>

      <div>
        <Btn type="submit" variant="primary" disabled={!canSubmit}>
          {submitting ? "Recording…" : "Record disclosure"}
        </Btn>
      </div>

      {result && (
        <div style={{ display: "grid", gap: 8, paddingTop: 4 }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Aligned</div>
              <Data size={14}>{fmt(result.taxonomy_aligned_pct)}%</Data>
            </div>
            <div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Enabling</div>
              <Data size={14} color={C.body}>{result.of_which_enabling_pct === null || result.of_which_enabling_pct === undefined ? "—" : `${fmt(result.of_which_enabling_pct)}%`}</Data>
            </div>
            <div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.body, marginBottom: 3 }}>Transitional</div>
              <Data size={14} color={C.body}>{result.of_which_transitional_pct === null || result.of_which_transitional_pct === undefined ? "—" : `${fmt(result.of_which_transitional_pct)}%`}</Data>
            </div>
          </div>
          {result.legal_basis && (
            <p style={{ fontFamily: MONO, fontSize: 11, color: C.muted, margin: 0, lineHeight: 1.5 }}>
              {result.legal_basis}
            </p>
          )}
          {result.dnsh_statement_required && (
            <p style={{ fontFamily: MONO, fontSize: 11, color: C.body, margin: 0, lineHeight: 1.5 }}>
              · DNSH statement required for the non-aligned share (Delegated Regulation (EU) 2022/1288).
            </p>
          )}
          {result.no_eu_criteria_statement_required && (
            <p style={{ fontFamily: MONO, fontSize: 11, color: C.body, margin: 0, lineHeight: 1.5 }}>
              · Art. 7 statement required: investments do not take EU sustainability criteria into account.
            </p>
          )}
        </div>
      )}
    </form>
  );
}
